import React, { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { RotateCcw } from "lucide-react";
import RichTextEditor from "@/components/RichTextEditor";

export default function CampaignResendDialog({ campaign, onClose, onDone }) {
  const [subject, setSubject] = useState(`${campaign.subject}`);
  const [html, setHtml] = useState("");
  const [scheduleOn, setScheduleOn] = useState(false);
  const [scheduleAt, setScheduleAt] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (start = false) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/bulk/campaigns/${campaign.id}/resend`, {
        subject,
        body_html: html,
        body_text: html.replace(/<[^>]+>/g, " "),
        schedule_at: scheduleOn && scheduleAt ? new Date(scheduleAt).toISOString() : null,
      });
      if (start) await api.post(`/bulk/campaigns/${data.id}/start`);
      toast.success(start ? "Resend started" : "Draft created");
      onDone?.();
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    setBusy(false);
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-2xl max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="resend-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl tracking-tight flex items-center gap-2"><RotateCcw className="h-5 w-5 text-emerald-brand" /> Resend to same recipients</DialogTitle>
          <p className="text-xs text-muted-foreground">Same recipient list from <span className="font-mono">{campaign.name}</span> · {campaign.total} recipients. New subject & body.</p>
        </DialogHeader>
        <div className="space-y-3">
          <F label="New subject"><Input value={subject} onChange={(e) => setSubject(e.target.value)} required data-testid="resend-subject" /></F>
          <F label="New body"><RichTextEditor value={html} onChange={setHtml} placeholder="Design your follow-up…" /></F>
          <div className="flex items-center justify-between border border-border rounded-xl px-3 py-2.5">
            <div>
              <div className="text-sm font-medium">Schedule</div>
              <div className="text-[11px] text-muted-foreground">Send later (UTC).</div>
            </div>
            <Switch checked={scheduleOn} onCheckedChange={setScheduleOn} data-testid="resend-schedule-toggle" />
          </div>
          {scheduleOn && <F label="Send at (UTC)"><Input type="datetime-local" value={scheduleAt} onChange={(e) => setScheduleAt(e.target.value)} data-testid="resend-schedule-at" /></F>}
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => submit(false)} disabled={busy} className="rounded-xl" data-testid="resend-save-draft">Save as draft</Button>
          <Button onClick={() => submit(true)} disabled={busy} className="rounded-xl" data-testid="resend-start">Save & start</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function F({ label, children }) {
  return <div className="space-y-1.5"><Label className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">{label}</Label>{children}</div>;
}
